// client/src/api/services/profileService.ts
import { apiClient } from '../client';
import { ENDPOINTS } from '../endpoints';
import { getErrorMessage } from '@/utils/errorUtils';
import { AxiosError } from "axios";

/**
 * Fetch profile details
 */
export const getDetails = async (): Promise<unknown> => {
  try {
    const response = await apiClient.get<unknown>(ENDPOINTS.PROFILE.GET_DETAILS);
    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching profile details:', error);
    throw new Error(getErrorMessage(error) || 'Failed to fetch profile details');
  }
};

export const changePassword = async (password): Promise<unknown> => {
  try {
    const response = await apiClient.put<unknown>(ENDPOINTS.PROFILE.CHANGE_PASSWORD, password);
    return response.data;
  } catch (error: unknown) {
    console.error('Error changing password:', error);
    throw new Error(getErrorMessage(error) || 'Failed to change password');
  }
};

export const DeviceLinked = async (page, limit): Promise<unknown> => {
  try {
    const response = await apiClient.get<unknown>(`${ENDPOINTS.PROFILE.DEVICE_LINKED}?page=${page}&limit=${limit}`);
    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching linked devices:', error);
    throw new Error(getErrorMessage(error) || 'Failed to fetch linked devices');
  }
};

export const GET_NOTIFICATION = async (): Promise<unknown> => {
  try {
    const response = await apiClient.get<unknown>(`${ENDPOINTS.PROFILE.GET_NOTIFICATION}`);
    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching notifications:', error);
    throw new Error(getErrorMessage(error) || 'Failed to fetch notifications');
  }
};

export const UPDATE_NOTIFICATION = async (data): Promise<unknown> => {
  try {
    const response = await apiClient.put<unknown>(`${ENDPOINTS.PROFILE.UPDATE_NOTIFICATION}`, data);
    return response.data;
  } catch (error: unknown) {
    console.error('Error updating notifications:', error);
    throw new Error(getErrorMessage(error) || 'Failed to update notifications');
  }
};

export const ADD_DEVICE = async (data): Promise<unknown> => {
  try {
    const response = await apiClient.post<unknown>(`${ENDPOINTS.PROFILE.ADD_DEVICE}`, data);
    return response.data;
  } catch (error: unknown) {
    console.error('Error adding device:', error);
    throw new Error(getErrorMessage(error) || 'Failed to add device');
  }
};

export const UPDATE_PROFILE_DETAILS = async (data): Promise<unknown> => {
  try {
    const response = await apiClient.put<unknown>(`${ENDPOINTS.PROFILE.UPDATE_DETAILS}`, data);
    return response.data;
  } catch (error: unknown) {
    console.error('Error updating profile details:', error);
    throw new Error(getErrorMessage(error) || 'Failed to update profile details');
  }
};

export const GET_TWOFA_STATUS = async (): Promise<unknown> => {
  try {
    const response = await apiClient.get<unknown>(`${ENDPOINTS.USER.TWO_FA.STATUS}`);
    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching 2FA status:', error);
    throw new Error(getErrorMessage(error) || 'Failed to fetch 2FA status');
  }
};

export const TWOAUTH_ENABLE = async (data: { email: string; method?: string }): Promise<unknown> => {
  try {
    const response = await apiClient.post<unknown>(`${ENDPOINTS.USER.TWO_FA.SETUP}`, data);
    return (response.data as unknown as Record<string, unknown>)?.data || response.data;
  } catch (error: unknown) {
    console.error('Error enabling 2FA:', error);
    throw new Error(getErrorMessage(error) || 'Failed to enable 2FA');
  }
};

export const TWOAUTH_VERIFY = async (data: { email: string; otp: string; method?: string }): Promise<unknown> => {
  try {
    const response = await apiClient.post<unknown>(`${ENDPOINTS.USER.TWO_FA.VERIFY}`, data);
    return response.data;
  } catch (error: unknown) {
    console.error('Error verifying 2FA:', error);
    throw new Error(getErrorMessage(error) || 'Failed to verify 2FA');
  }
};

export const TWOAUTH_DISABLE = async (data?: { password?: string }): Promise<unknown> => {
  try {
    const response = await apiClient.post<unknown>(`${ENDPOINTS.PROFILE.TWOAUTH_DISABLE}`, data);
    return response.data;
  } catch (error: unknown) {
    console.error('Error disabling 2FA:', error);
    throw new Error(getErrorMessage(error) || 'Failed to disable 2FA');
  }
};

export const UPLOAD_PROFILE = async (data: FormData): Promise<unknown> => {
  try {
    const response = await apiClient.post(
      ENDPOINTS.PROFILE.UPLOAD_PHOTO,
      data,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        transformRequest: (d) => d,
      }
    );

    return response.data;
  } catch (error: unknown) {
    console.error("Upload profile failed:", error);
    throw new Error(getErrorMessage(error) || "Upload failed");
  }
};


export const REVOKE_DEVICE = async (id): Promise<unknown> => {
  try {
    const response = await apiClient.delete<unknown>(`${ENDPOINTS.PROFILE.REVOKE_DEVICE(id)}`);
    return response.data;
  } catch (error: unknown) {
    console.error('Error revoking device:', error);
    throw new Error(getErrorMessage(error) || 'Failed to revoke device');
  }
};

export default {
  getDetails,
  changePassword,
  DeviceLinked,
  REVOKE_DEVICE,
  GET_NOTIFICATION,
  UPDATE_NOTIFICATION,
  ADD_DEVICE,
  UPDATE_PROFILE_DETAILS,
  GET_TWOFA_STATUS,
  TWOAUTH_ENABLE,
  TWOAUTH_VERIFY,
  TWOAUTH_DISABLE,
};
